import { html, LitElement } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { classMap } from 'lit/directives/class-map.js';
import '@ui5/webcomponents/dist/Table.js';
import '@ui5/webcomponents/dist/TableColumn.js';
import '@ui5/webcomponents/dist/TableRow.js';
import '@ui5/webcomponents/dist/TableCell.js';
import '@ui5/webcomponents/dist/TableGroupRow.js';
import '@ui5/webcomponents/dist/CheckBox.js';
import closeIcon from '@getgo/chameleon-icons/dist/rebranded/close-filled.svg';
import ellipsisIcon from '@getgo/chameleon-icons/dist/rebranded/more-menu-vertical-filled.svg';
import caretDownIcon from '@getgo/chameleon-icons/dist/rebranded/chevron-down-filled.svg';
import caretUpIcon from '@getgo/chameleon-icons/dist/rebranded/chevron-up-filled.svg';
import tableStyle from './table-style.js';
import { checkboxStyles } from '../../styles/synapse-checkbox.js';
import { spacing } from '../../styles/synapse-spacing.js';
import typography from '../../styles/synapse-typography.js';
import buttons from '../../styles/synapse-buttons.js';
import tooltip from '../../styles/synapse-tooltip.js';
import '../synapse-drawer/synapse-drawer.js';
import '../synapse-checkbox/synapse-checkbox.js';
import '../synapse-icon/synapse-icon.js';
/**
 * @typedef {function} RowClickHandler
 * @param {Object} rowData
 * @return {Event}
 *
 * @typedef {function} GroupClickHandler
 * @param {string} groupId
 * @return {Event}
 *
 * @typedef {function} SortHandler
 * @param {Object} sortArgs
 * @return {Event}
 *
 * @typedef {function} FooterClickHandler
 * @return {Event}
 *
 * @typedef {function} ActionsRenderHandler
 * @param {Object} rowData
 * @return {Event}
 *
 * @typedef {Object} TableEvents
 * @property {RowClickHandler} rowClicked
 * @property {GroupClickHandler} groupClicked
 * @property {SortHandler} sort
 * @property {FooterClickHandler} footerClicked
 * @property {ActionsRenderHandler} renderActions
 *
 */
/** @typedef {import('types/synapse-data-grid').Field} Field */
export default class SynapseDataGrid extends LitElement {
    static get properties() {
        return {
            data: { type: Array },
            innerData: { type: Array },
            summaryData: { type: Array },
            fields: { type: Object },
            enableDrillDown: { type: Boolean, attribute: 'enable-drill-down' },
            fixedHeader: { type: Array, attribute: 'fixed-header' },
            withColumnSelector: { type: Boolean, attribute: 'with-column-selector' },
            withLoadMoreBtn: { type: Boolean, attribute: 'with-load-more-btn' },
            moreBtnText: { type: String, attribute: 'more-btn-text' },
            withFooter: { type: Boolean, attribute: 'with-footer' },
            useLocalSort: { type: Boolean, attribute: 'use-local-sort' },
            disabledColumns: { type: Array },
            tableEvents: { type: Object, attribute: 'table-events' },
            groupBy: { type: String, attribute: 'group-by' },
            theme: { type: String },
            noDataText: { type: String, attribute: 'no-data-text' },
            selectColumnsDrawerTitle: { type: String, attribute: 'select-columns-drawer-title' },
            loading: { type: Boolean, reflect: true },
            columnSelectorDrawerOpen: { type: Boolean },
            multiSelect: { type: Boolean, attribute: 'multi-select' },
            selectCover: { type: Function },
            pageStart: { type: Number, attribute: 'page-start' },
            pageEnd: { type: Number, attribute: 'page-end' },
            selectedRowIds: { type: Array, attribute: 'selected-row-ids' },
            highlightedRowIds: { type: Array, attribute: 'highlighted-row-ids' },
            rowIdFieldName: { type: String, attribute: 'row-id-field-name' },
            paginationGroupping: { type: Boolean, attribute: 'pagination-groupping' },
            withGroupSelector: { type: Boolean, attribute: 'with-group-selector' },
            pageSizeHeight: { type: Boolean, attribute: 'page-size-height' },
            selectedGroupIds: { type: Array, attribute: 'selected-group-ids' },
        };
    }
    static get styles() {
        return [checkboxStyles, spacing, typography, buttons, tableStyle, tooltip];
    }
    constructor() {
        super();
        /** @type {Array<Object>} */
        this.data = [];
        /** @private  */
        this.innerData = [];
        this.summaryData = [];
        /** @type {Object.<string, Field>} */
        this.fields = {};
        this.enableDrillDown = false;
        this.withFooter = false;
        this.withColumnSelector = false;
        this.withLoadMoreBtn = false;
        this.moreBtnText = 'Show more devices';
        this.sortedField = null;
        this.sortDirection = null;
        this.useLocalSort = false;
        this.noDataText = 'There were no sessions in the selected time range.';
        this.selectColumnsDrawerTitle = 'Select Columns';
        /** @type {Array<string>} */
        this.fixedHeader = [];
        this.loading = false;
        this.columnSelectorDrawerOpen = false;
        this.multiSelect = false;
        this.closeDrawerTimer = null;
        this.pageStart = 0;
        this.pageEnd = 0;
        /** @type {TableEvents} */
        this.tableEvents = {};
        /** @type { string[] } */
        this.selectedRowIds = [];
        /** @type { string[] } */
        this.highlightedRowIds = [];
        /** @type { string } */
        this.rowIdFieldName = 'id';
        this.paginationGroupping = false;
        this.withGroupSelector = false;
        this.selectedGroupIds = [];
        this.pageSizeHeight = false;
    }
    // eslint-disable-next-line class-methods-use-this
    get disabledColumns() {
        return JSON.parse(window.localStorage.getItem('disabledColumns') || '[]');
    }
    set disabledColumns(value) {
        window.localStorage.setItem('disabledColumns', JSON.stringify(value));
        this.requestUpdate();
    }
    connectedCallback() {
        super.connectedCallback();
        this.clearSelectionListener = this.clearSelection.bind(this);
        window.addEventListener('clear-selection', this.clearSelectionListener);
    }
    disconnectedCallback() {
        window.removeEventListener('clear-selection', this.clearSelectionListener);
    }
    /**
     *
     * @param {Map} changedProperties
     */
    update(changedProperties) {
        if (changedProperties.has('data') && this.data instanceof Array) {
            let { data } = this;
            if (this.useLocalSort) {
                const field = this.getSortedField();
                if (field) {
                    const [fieldName, props] = field;
                    data = this.sortData(props.direction, fieldName, props, this.data);
                }
            }
            this.innerData = [...data];
        }
        super.update(changedProperties);
    }
    // eslint-disable-next-line class-methods-use-this
    uniq(arr) {
        const s = new Set(arr);
        const it = s.values();
        return Array.from(it);
    }
    exposeEvent() {
        this.dispatchEvent(new CustomEvent('load-more', { bubbles: true, composed: true }));
    }
    render() {
        return html `
      <synapse-drawer
        header-title=${this.selectColumnsDrawerTitle}
        ?open="${this.columnSelectorDrawerOpen}"
        @drawer-close=${this.closeSelectorDrawer}
      >
        <div class="icon-button" slot="close" @click=${this.closeSelectorDrawer}>
          <synapse-icon svg="${closeIcon}"></synapse-icon>
        </div>
        <div class="drawer-body" tabindex="-1">${this.getColumnCheckboxActions()}</div>
      </synapse-drawer>
      <div id="table-container" style="height: ${this.tableHeight}">
        <div part="container" class="container">
          ${html `
            <ui5-table
              mode=${this.multiSelect ? 'MultiSelect' : ''}
              growing=${this.withLoadMoreBtn ? 'Button' : ''}
              growing-button-text=${this.moreBtnText}
              no-data-text=${this.noDataText}
              sticky-column-header="true"
              class=${classMap({ 'demo-table': true, drillDownTable: this.enableDrillDown })}
              @load-more=${this.exposeEvent}
              @selection-change=${e => this.handleSelectionChange(e)}
            >
              ${this.getHeader()} ${this.groupBy ? html `${this.getGroupByBody()}` : this.getDefaultBody()}
              ${this.withFooter && this.summaryData.length ? html ` ${this.getFooter()} ` : null}
            </ui5-table>
          `}
          ${this.loading ? html ` <synapse-spinner></synapse-spinner> ` : ''}
        </div>
      </div>
    `;
    }
    get pagedInnerData() {
        return this.innerData.slice(this.pageStart, this.pageEnd + 1);
    }
    get tableHeight() {
        if (!this.pageSizeHeight) {
            return 'auto';
        }
        const rowNumber = this.pagedInnerData.length + 1;
        const maxRowNumber = this.pageEnd - this.pageStart + 2;
        const realRowNumber = this.pageStart === 0 ? rowNumber : maxRowNumber;
        return `${realRowNumber * 56.5}px`;
    }
    handleSelectionChange(e) {
        if (this.withGroupSelector) {
            this.selectedGroupIds = [];
            this.dispatchEvent(this.tableEvents?.groupClicked({ groupIds: [] }));
        }
        if (this.tableEvents?.selectionChange) {
            this.tableEvents?.selectionChange(e);
        }
    }
    getGroupByBody() {
        const referenceData = this.paginationGroupping ? this.pagedInnerData : this.innerData;
        return this.uniq(referenceData.map(data => (typeof this.groupBy === 'function' ? this.groupBy(data) : data[this.groupBy])))
            .sort()
            .map(groupVal => {
            const rows = referenceData.filter(row => typeof this.groupBy === 'function' ? this.groupBy(row) === groupVal : row[this.groupBy] === groupVal);
            const groupHeader = this.getGroupHeader(rows.length, groupVal, rows);
            return html `${groupHeader}
        ${repeat(rows, row => row[this.rowIdFieldName] || row.name, row => this.getBody(row))}`;
        });
    }
    getDefaultBody() {
        if (this.paginationGroupping) {
            return this.groupPagedInnerData();
        }
        return repeat(!this.pageEnd ? this.innerData : this.pagedInnerData, row => row[this.rowIdFieldName] || row.name, row => this.getBody(row));
    }
    async sort(fieldKey, field, event) {
        this.loading = true;
        this.sortedField = fieldKey;
        const target = event.currentTarget;
        const { direction } = target.dataset;
        const nextDirection = {
            asc: 'desc',
            desc: 'asc',
        };
        if (this.useLocalSort) {
            // reset direction on every prop
            Object.values(this.fields).forEach(props => {
                // eslint-disable-next-line no-param-reassign
                props.direction = null;
                if (props.subfields) {
                    // eslint-disable-next-line no-param-reassign
                    props.subfields[props.sortBy].direction = null;
                }
            });
            target.dataset.direction = nextDirection[direction];
            // eslint-disable-next-line no-param-reassign
            field.direction = nextDirection[direction];
            this.sortDirection = nextDirection[direction];
            this.innerData = this.sortData(nextDirection[direction], fieldKey, field, this.data);
            this.dispatchEvent(new CustomEvent('local-sort', {
                detail: {
                    key: fieldKey,
                    direction: nextDirection[direction],
                },
            }));
        }
        else {
            this.dispatchEvent(this.tableEvents?.sort({
                key: fieldKey,
                direction: nextDirection[direction],
            }));
        }
        this.loading = false;
    }
    /**
     *
     * @param {string} direction : ;
     * @param {string} fieldKey
     * @param {Field} field
     * @param {any[]} [data]
     */
    sortData(direction, fieldKey, field, data) {
        return [...data].sort((row1, row2) => {
            const order = direction === 'asc' ? 1 : -1;
            const hasSortKeyFn = 'sortKeyFn' in field;
            const data1 = !hasSortKeyFn ? row1[fieldKey] : field.sortKeyFn(row1[fieldKey], row1);
            const data2 = !hasSortKeyFn ? row2[fieldKey] : field.sortKeyFn(row2[fieldKey], row2);
            const a = data1?.toLowerCase ? data1.toLowerCase() : data1;
            const b = data2?.toLowerCase ? data2.toLowerCase() : data2;
            const aIsHighlighted = this.highlightedRowIds.includes(row1[this.rowIdFieldName]);
            const bIsHighlighted = this.highlightedRowIds.includes(row2[this.rowIdFieldName]);
            if ((aIsHighlighted || bIsHighlighted) && !(aIsHighlighted && bIsHighlighted)) {
                // To put highlights on top of the list
                return -(aIsHighlighted > bIsHighlighted);
            }
            return (order *
                ((b === null || b === undefined || b === '') - (a === null || a === undefined || a === '') ||
                    +(a > b) ||
                    -(a < b)));
        });
    }
    /**
     *
     * @returns { [string, Field]}
     */
    getSortedField() {
        const field = Object.entries(this.fields).find(([fieldKey, fieldProps]) => fieldKey === this.sortedField || fieldProps.sortBy === this.sortedField || fieldProps.direction);
        if (field) {
            const [, fieldProps] = field;
            if (fieldProps?.subfields) {
                return [this.sortedField, fieldProps?.subfields[this.sortedField]];
            }
        }
        if (field && !field[1].direction) {
            field[1].direction = this.sortDirection;
        }
        return field;
    }
    getFooter() {
        if (!this.summaryData.length) {
            return html ``;
        }
        const summary = this.summaryData[0];
        const summaryCols = Object.entries(this.fields).map(([fieldName, props], index) => {
            if (index === 0) {
                return html ` <ui5-table-cell>Total</ui5-table-cell> `;
            }
            const converter = props.converter || (e => e);
            return html ` <ui5-table-cell class="${props.type}">${converter(summary[fieldName])}</ui5-table-cell> `;
        });
        if (this.withColumnSelector) {
            summaryCols.push(html ` <ui5-table-cell></ui5-table-cell> `);
        }
        return html `
      <ui5-table-row
        class="footer"
        @click=${() => this.enableDrillDown && this.dispatchEvent(this.tableEvents?.footerClicked())}
      >
        ${summaryCols}
      </ui5-table-row>
    `;
    }
    getHeader() {
        const cols = Object.entries(this.fields || [])
            .filter(([key]) => !this.disabledColumns.includes(key) && this.groupBy !== key)
            .map(([key, field]) => {
            const sortByKey = field.sortBy || key;
            const sortByField = field.sortBy ? field.subfields[field.sortBy] : field;
            const direction = this.sortedField === sortByKey && !this.useLocalSort ? this.sortDirection : sortByField.direction;
            return html `
          <ui5-table-column
            id="${key}-header"
            data-direction=${direction || 'asc'}
            @click=${event => {
                if (field.sortable !== false)
                    this.sort(sortByKey, sortByField, event);
            }}
            class=${classMap({ [field.type]: true, sortedColumnHeader: this.sortedField === sortByKey })}
            slot="columns"
            min-width=${this.fixedHeader.includes(key) ? 0 : 650}
            ?demand-popin=${!this.fixedHeader.includes(key)}
            popin-text="${field.name}"
          >
            <div class="header-container headline-4">
              <span class="title-container">
                <span>${field.header || field.name}</span>
                ${field.sortable !== false
                ? html `
                      <synapse-icon
                        class="sort-icon ${direction ? 'activeSort' : ''}"
                        svg="${direction === 'asc' || !direction ? caretUpIcon : caretDownIcon}"
                      ></synapse-icon>
                    `
                : ''}
              </span>
            </div>
          </ui5-table-column>
        `;
        });
        if (this.withColumnSelector) {
            cols.push(html `
        <ui5-table-column class="action" part="action-header" slot="columns">
          <span id="columnSelector" class="icon-button">
            <div class="toggle" slot="toggle" @click=${this.toggleSelectorDrawer}>
              <synapse-icon svg=${ellipsisIcon}></synapse-icon></div
          ></span>
        </ui5-table-column>
      `);
        }
        return cols;
    }
    getGroupHeader(childCount, value, rows) {
        // if value is true/false then only the 'true' part has header with title of the property name
        if (value === false) {
            return '';
        }
        const groupName = value === true ? this.fields[this.groupBy]?.name || this.groupBy : value;
        const counter = html `${childCount > 1 && !this.paginationGroupping ? `(${childCount})` : ''}`;
        return html `
      <ui5-table-group-row>
        ${this.withGroupSelector
            ? html `
              <ui5-table-cell class="checkbox-container group-checkbox">
                <ui5-checkbox
                  class="ui5-multi-select-checkbox"
                  tabindex="-1"
                  aria-label="Item selection"
                  ui5-checkbox=""
                  ?checked=${this.selectedGroupIds.includes(value)}
                  @click=${e => this.dispatchGroupSelectedEvent(e.target, rows, value)}
                ></ui5-checkbox>
                <b>${groupName} ${counter}</b>
              </ui5-table-cell>
            `
            : html `<b>${groupName} ${counter}</b>`}
      </ui5-table-group-row>
    `;
    }
    getBody(row) {
        const cols = Object.entries(this.fields)
            .filter(([key, field]) => !this.disabledColumns.includes(key) && field.type !== 'icon' && this.groupBy !== key)
            .map(([key, prop]) => {
            if (prop.subfields)
                return html `
            <ui5-table-cell class="${prop.type}">
              ${SynapseDataGrid.renderSubFields(row, prop.subfields)}
            </ui5-table-cell>
          `;
            const convertedValue = prop?.converter ? prop?.converter(row[key], row) : row[key];
            return html ` <ui5-table-cell class="${prop.type}">${convertedValue}</ui5-table-cell> `;
        });
        cols.push(this.tableEvents?.renderActions
            ? html `<ui5-table-cell class="row-actions">
            <div class="row-actions-container" part="row-actions">${this.tableEvents.renderActions(row)}</div>
          </ui5-table-cell>`
            : html ` <ui5-table-cell class="selector"> </ui5-table-cell> `);
        return html `
      <ui5-table-row
        .data=${row}
        id=${row[this.rowIdFieldName]}
        @click=${event => {
            if (event
                .composedPath()
                .find(el => ['row-actions-container', 'ui5-multi-select-checkbox'].includes(el.className))) {
                return;
            }
            if (this.enableDrillDown)
                this.dispatchEvent(this.tableEvents?.rowClicked(row));
        }}
        ?selected=${this.selectedRowIds.includes(row[this.rowIdFieldName])}
        class=${classMap({
            highlighted: this.highlightedRowIds.includes(row[this.rowIdFieldName]),
            'disabled-row': row.disabled,
        })}
      >
        ${cols}
      </ui5-table-row>
    `;
    }
    dispatchGroupSelectedEvent(target, rows, groupId) {
        if (target.checked) {
            this.selectedRowIds = Array.from(new Set([...(this.selectedRowIds || []), ...rows.map(row => row.id)]));
            this.selectedGroupIds = [...this.selectedGroupIds, groupId];
        }
        else {
            const ids = rows.map(row => row.id);
            this.selectedRowIds = this.selectedRowIds.filter(selectedRow => !ids.includes(selectedRow));
            this.selectedGroupIds = this.selectedGroupIds.filter(id => id !== groupId);
        }
        this.dispatchEvent(this.tableEvents?.groupClicked({ groupIds: this.selectedGroupIds }));
    }
    clearSelection() {
        const table = this.shadowRoot.querySelector('ui5-table');
        const previouslySelectedRows = table.rows.filter(row => row.selected);
        table.rows.forEach(row => {
            // eslint-disable-next-line no-param-reassign
            row.selected = false;
        });
        // eslint-disable-next-line no-underscore-dangle
        table._allRowsSelected = false;
        table.fireEvent('selection-change', { selectedRows: [], previouslySelectedRows });
    }
    static renderSubFields(row, fields) {
        return html `
      <div part="subfield-container">
        ${repeat(Object.values(fields), field => field.type, field => field.converter(row))}
      </div>
    `;
    }
    getColumnCheckboxActions() {
        this.disabledColumns = this.disabledColumns.filter(column => column !== 'Device nickname');
        return Object.entries(this.fields)
            .filter(([, field]) => !field.type?.includes('icon') && field.type !== this.groupBy)
            .map(([key, prop]) => html `<synapse-checkbox
            large
            data-id=${key}
            @change=${e => {
            if (!e.detail.isChecked) {
                this.disabledColumns = [...this.disabledColumns, key];
            }
            else {
                this.disabledColumns = this.disabledColumns.filter(columnKey => columnKey !== key);
            }
        }}
            label-text=${prop.name}
            ?disabled=${prop.columnSelector === false}
            default-checked="${!this.disabledColumns.includes(key)}"
          ></synapse-checkbox>`);
    }
    toggleSelectorDrawer() {
        this.columnSelectorDrawerOpen = !this.columnSelectorDrawerOpen;
    }
    closeSelectorDrawer(event) {
        if (event?.detail?.event?.composedPath().some(el => el.id === 'columnSelector')) {
            return;
        }
        this.columnSelectorDrawerOpen = false;
    }
}
window.customElements.define('synapse-data-grid', SynapseDataGrid);
